import React, { useEffect, useState } from "react";
import Grid from "@material-ui/core/Grid";
import { Box, Card, ListItem, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { map, pluck, sum } from "ramda";
import { notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../PanelLoadingIndicator";
import ListItemText from "@material-ui/core/ListItemText";
import List from "@material-ui/core/List";
import { useCspSubmissionCount } from "../../creator-application/cspServices";
import { calculateDate } from "./DateRangeSelect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileSpreadsheet } from "@fortawesome/pro-solid-svg-icons";
import CspSubmissionReportDialog from "../../creator-application/dashboard/CspSubmissionReportDialog";
import IconButton from "@material-ui/core/IconButton";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    // minWidth: 347,
    height: "100%",
    minHeight: 595,
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    borderRadius: 20,
    color: theme.palette.secondary.contrastText,
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
  },

  h1: {
    fontSize: "3.6rem",
    lineHeight: "3.4rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },

  h4: {
    color: theme.palette.primary.main,
  },

  divider: {
    borderBottomStyle: "dotted",
    borderBottomWidth: 3,
    borderBottomColor: theme.palette.secondary.main,
  },

  listItemText: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minWidth: 268,
  },

  listItemTextPrimary: {
    fontSize: "1.4rem",
    lineHeight: "3.4rem",
    color: theme.palette.primary.darker,
    textTransform: "none",
    paddingRight: 30,
    // maxWidth: 270,
  },

  listItemTextSecondary: {
    fontSize: "1.4rem",
    lineHeight: "3.4rem",
    color: theme.palette.primary.darker,
    textTransform: "none",
  },

  progressWrapper: {
    flexGrow: 1,
  },

  cspList: {
    padding: 0,
    paddingRight: theme.spacing(4),
    overflow: "auto",
    maxHeight: 475,
  },

  allButton: {
    fontSize: "1.4rem",
    lineHeight: "1.8rem",
    fontWeight: "bold",
    paddingTop: theme.spacing(2),
    color: theme.palette.secondary.contrastText,
  },

  forwardIcon: {
    color: theme.palette.secondary.contrastText,

    "& :first-child": {
      fontSize: "1.4rem",
      lineHeight: "1.8rem",
      fontWeight: "bold",
      color: theme.palette.secondary.contrastText,
    },
  },

  body1: {
    fontStyle: "italic",
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
    color: theme.palette.secondary.contrastText,
  },

  icon: {
    color: theme.palette.primary.main,
  },
}));

const CspProjectSubmissionCard = ({ title, range }) => {
  const classes = useStyles();
  const [totalCount, setTotalCount] = useState(0);
  const countResults = useCspSubmissionCount(
    notBlank(range) ? calculateDate(range) : null,
  );

  useEffect(() => {
    if (countResults.isSuccess) {
      const total = sum(pluck("count", countResults.data));
      setTotalCount(total);
    } else {
      setTotalCount(0);
    }
  }, [countResults.isSuccess, countResults.data]);

  const [listRef, setListRef] = useState(null);
  const [scrollVisible, setScrollVisible] = useState(false);
  useEffect(() => {
    if (listRef) {
      const scrollVisible = listRef.scrollHeight > listRef.clientHeight;
      setScrollVisible(scrollVisible);
    }
  }, [listRef]);

  const [open, setOpen] = useState(false);
  const handleReportClick = event => {
    event.stopPropagation();
    setOpen(true);
  };
  const handleReportClose = () => setOpen(false);

  return (
    <Card classes={{ root: classes.root }}>
      <CspSubmissionReportDialog
        open={open}
        projects={countResults.data}
        handleClose={handleReportClose}
      />
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4" component="div" classes={{ h4: classes.h4 }}>
            {title || "CSP Submissions"}
          </Typography>
        </Grid>
        <Grid item>
          <Tooltip title="Download Report" placement="bottom-start">
            <IconButton onClick={handleReportClick}>
              <FontAwesomeIcon
                className={classes.icon}
                icon={faFileSpreadsheet}
              />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Box className={classes.divider} paddingBottom="25px">
        <Typography variant="h1" component="span" classes={{ h1: classes.h1 }}>
          {countResults.isSuccess ? totalCount.toLocaleString() : "Loading..."}
        </Typography>
      </Box>
      {countResults.isSuccess ? (
        notBlank(countResults.data) ? (
          <Box>
            <List ref={setListRef} classes={{ root: classes.cspList }}>
              {map(
                subCount => (
                  <ListItem
                    key={subCount.projectName}
                    disableGutters
                    divider
                    classes={{
                      divider: classes.divider,
                    }}
                  >
                    <Tooltip
                      title={subCount.projectName}
                      placement="bottom-start"
                    >
                      <ListItemText
                        primaryTypographyProps={{ noWrap: true }}
                        style={{ paddingRight: scrollVisible ? 5 : 0 }}
                        classes={{
                          root: classes.listItemText,
                          primary: classes.listItemTextPrimary,
                          secondary: classes.listItemTextSecondary,
                        }}
                        primary={subCount.projectName}
                        secondary={subCount.count}
                      />
                    </Tooltip>
                  </ListItem>
                ),
                countResults.data,
              )}
            </List>
          </Box>
        ) : (
          <Box height={65} ml={"auto"} mr={"auto"} mt={12} textAlign="center">
            <Typography
              component="div"
              variant="body1"
              classes={{ body1: classes.body1 }}
            >
              You have no CSP Projects. Please contact support to set up a new
              Project.
            </Typography>
          </Box>
        )
      ) : (
        <Box flexGrow={1}>
          <PanelLoadingIndicator color="secondary" />
        </Box>
      )}
    </Card>
  );
};

export default CspProjectSubmissionCard;
